import { CONTINUE_TOUR_BUTTON_TEXT } from './helpers';

const PLACES_PAGE_SIZE = 5;

let messageIdCounter = 0;
export const nextMessageId = () => {
  messageIdCounter += 1;
  return Date.now() + messageIdCounter;
};
const AI_NO_PLACES_FALLBACK = "There are some amenities that match your question, but they don't have location data available for navigation on the map. Please speak with us for more information about these amenities.";
const AI_ERROR_FALLBACK = "We're having trouble processing your question right now. Please try again or speak with us for assistance.";

// step4 promises "here's a selection of tour stops", so it must not be sent
// when the keyword lookup came back empty (#1255). Say so plainly instead, and
// keep the visitor moving with the same two routes the more-places prompt
// offers.
export const STEP4_NO_PLACES_TEXT = "I couldn't find any tour stops matching that just now. Tell me about another interest and I'll look again, or ask me a question about the community.";

/**
 * The honest empty-result text, addressed by name when we have one. The text
 * opens with "I", so its first letter is kept as written (#1650).
 */
export const buildNoPlacesStepText = (firstName) => (firstName
  ? `${firstName}, ${STEP4_NO_PLACES_TEXT}`
  : STEP4_NO_PLACES_TEXT);
export const STEP4_NO_PLACES_INSTRUCTION = 'Click an option below:';
export const STEP4_NO_PLACES_BUTTONS = [
  { text: 'Share Another Interest', action: 'decline_more_places' },
  { text: 'Ask a Question', action: 'ask_question', nextStep: 'step_ask_question' },
];

/**
 * Filter places to only those with valid svg_id for map navigation.
 */
export const filterNavigablePlaces = (places) => places.filter((place) => {
  const hasSvgId = place.svg_id
    && typeof place.svg_id === 'string'
    && place.svg_id.trim() !== ''
    && place.svg_id.trim() !== '0'
    && place.svg_id.toLowerCase().trim() !== 'null';

  return hasSvgId;
});

/**
 * Split places into the current page and remaining places.
 */
export const paginatePlaces = (places) => {
  const currentPage = places.slice(0, PLACES_PAGE_SIZE);
  const remaining = places.slice(PLACES_PAGE_SIZE);
  return { currentPage, remaining };
};

/**
 * Replace dynamic placeholders in a bot message string.
 */
export const replacePlaceholders = (message, state, config = {}) => {
  let result = message;

  if (result.includes('[PREVIOUS INTERESTS]') && state.selectedInterests.length > 0) {
    const interestLabels = state.selectedInterests.map((i) => (typeof i === 'string' ? i : i.label));
    result = result.replace('[PREVIOUS INTERESTS]', interestLabels.join(', '));
  }

  if (result.includes('[FIRST_NAME]') && state.userInfo.firstName) {
    result = result.replace('[FIRST_NAME]', state.userInfo.firstName);
  }

  if (result.includes('[COMMUNITY_NAME]') && config.communityName) {
    result = result.replace(/\[COMMUNITY_NAME\]/g, `<strong>${config.communityName}</strong>`);
  }

  return result;
};

/**
 * Build the bot response message when AI returns navigable places.
 */
export const buildPlacesResponse = (aiResponse, navigablePlaces, firstName, config = {}) => {
  const { currentPage } = paginatePlaces(navigablePlaces);
  const placeButtons = currentPage.map((place) => ({
    text: place.title,
    action: 'view_amenity',
    amenity: String(place.id),
  }));

  if (!aiResponse.response || currentPage.length === 0) {
    return {
      id: nextMessageId(),
      type: 'bot',
      text: AI_ERROR_FALLBACK,
      stepType: 'error',
    };
  }

  const mainText = replacePlaceholders(
    aiResponse.response,
    { selectedInterests: [], userInfo: { firstName } },
    config,
  );
  const instructions = currentPage.length === 1
    ? 'Click below to visit:'
    : 'Click any amenity below:';

  return {
    id: nextMessageId(),
    type: 'bot',
    text: mainText,
    instructions,
    buttons: placeButtons,
    stepType: 'ai_places',
  };
};

/**
 * Build the bot response message when no navigable places are found.
 *
 * showFollowUpButtons is set by the backend on the health-boundary reply
 * (#1508): it deliberately carries no places, and must not leave the
 * visitor at a dead end, so it gets the same two routes as an empty step4.
 */
export const buildNoPlacesResponse = (aiResponse) => {
  const message = {
    id: nextMessageId(),
    type: 'bot',
    text: aiResponse.response || AI_NO_PLACES_FALLBACK,
    stepType: 'ai_text',
  };

  if (aiResponse.showFollowUpButtons) {
    message.instructions = STEP4_NO_PLACES_INSTRUCTION;
    message.buttons = STEP4_NO_PLACES_BUTTONS;
    message.stepType = 'ai_follow_up_buttons';
  }

  return message;
};

/**
 * Build a regular text-only AI response (no place buttons).
 */
export const buildTextResponse = (aiResponse) => ({
  id: nextMessageId(),
  type: 'bot',
  text: aiResponse.response || AI_ERROR_FALLBACK,
});

/**
 * Build deferred external locations message with a "See Locations" button.
 */
export const buildDeferredExternalMessage = (deferredExternal) => {
  const instructions = deferredExternal.externalLocations?.length > 1
    ? 'Click below to view locations on the map:'
    : 'Click below to view on the map:';

  const buttonText = deferredExternal.externalLocations
    && deferredExternal.externalLocations.length > 1
    ? 'Area Locations'
    : 'Area Location';

  return {
    id: nextMessageId(),
    type: 'bot',
    text: deferredExternal.response,
    instructions,
    buttons: [{
      text: buttonText,
      action: 'see_external_locations',
      externalLocations: deferredExternal.externalLocations,
    }],
    continueButton: { text: CONTINUE_TOUR_BUTTON_TEXT, action: 'flush_deferred' },
    stepType: 'deferred_external',
  };
};

/**
 * Build a follow-up question message.
 */
export const buildFollowUpMessage = (followUpQuestion) => ({
  id: nextMessageId(),
  type: 'bot',
  text: followUpQuestion,
  stepType: 'ai_followup',
});

/**
 * Build an additional info message (shown after a delay).
 */
export const buildAdditionalInfoMessage = (additionalInfo) => ({
  id: nextMessageId(),
  type: 'bot',
  text: additionalInfo,
  stepType: 'additional_info',
});

/**
 * Build the buttons array for a conversation step, using dynamic data when appropriate.
 */
export const buildStepButtons = (nextStep, places) => {
  let buttons = nextStep.buttons || [];

  if (nextStep.id === 'step4' && places.length > 0) {
    const { currentPage } = paginatePlaces(places);
    buttons = currentPage.map((place) => ({
      text: place.title,
      action: 'view_amenity',
      amenity: place.id,
    }));
  }

  return buttons;
};

/**
 * Build a complete bot response from a conversation step definition.
 */
export const buildStepResponse = (nextStep, state, config, places) => {
  const isEmptyStep4 = nextStep.id === 'step4' && (places || []).length === 0;
  const interestLabels = state?.selectedInterests
    ?.map((i) => (typeof i === 'string' ? i : i.label));
  const interests = interestLabels?.join(', ') || '';
  const stepMessage = typeof nextStep.message === 'function'
    ? nextStep.message(config?.communityName, interests)
    : nextStep.message;
  const messageText = isEmptyStep4
    ? buildNoPlacesStepText(state?.userInfo?.firstName)
    : stepMessage;
  const botMessage = replacePlaceholders(messageText, state, config);
  const buttons = isEmptyStep4
    ? STEP4_NO_PLACES_BUTTONS
    : buildStepButtons(nextStep, places);

  return {
    id: nextMessageId(),
    type: 'bot',
    text: botMessage,
    instructions: isEmptyStep4
      ? STEP4_NO_PLACES_INSTRUCTION
      : (nextStep.instructions || null),
    buttons,
    continueButton: isEmptyStep4 ? null : (nextStep.continueButton || null),
    stepType: isEmptyStep4 ? 'step4_no_places' : nextStep.type,
  };
};
